import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Icon,
  Select,
  Tooltip,
  cn,
  type IconName,
  type SelectOption,
} from '@forecastify/ui';
import { usePageChromeState } from './page-chrome';
import { SELECT_SKIN, TONE } from './tones';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The Control Panel's shell, ported from the redesign's markup.             │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **ITS OWN SHELL, NOT `packages/ui`'s `AppShell`, AND THE REASON IS THAT THE TWO DRAWINGS
 * DIFFER.** The redesign's console is an *inset* layout — a muted sidebar, and the page sitting in a
 * white rounded card with an 8px gutter — with an environment line under the name, a theme picker
 * and a language switch in the footer, a sign-out button beside the operator, and dot badges on a
 * 52px rail when collapsed. The product's shell draws none of that. Teaching `AppShell` all of it
 * would change the product to serve the console; ported here, every class below is the mockup's,
 * verbatim, and the product does not move.
 *
 * ⚠️⚠️ **THE FIRST VERSION OF THIS CONSOLE WENT THROUGH `AppShell` AND LOOKED NOTHING LIKE THE
 * MOCKUP — and its collapse button did nothing at all.** Built from the mockup's *source* and never
 * put side by side with it. This file was written against screenshots of both.
 *
 * ⚠️ **Every row is a `<Link>`, not the mockup's `<button>`**, because a place you can go has an
 * address: a middle-click opens it, and the route suite's reachability check can see it. The base
 * stylesheet paints every `<a>` with `--link`, so each one sets the sidebar's own ink back.
 *
 * ⚠️ **Below 900px the sidebar hides**, which is the mockup's `max-[900px]:hidden` and nothing
 * more. The mockup draws no replacement for a narrow screen; this does not invent one.
 */

export interface ShellItem {
  readonly label: string;
  readonly icon: IconName;
  readonly href: string;
  readonly active: boolean;
  /** A count of things to go and do. Absent, the row has no badge at all. */
  readonly badge?: number;
  readonly badgeTone?: 'muted' | 'bad' | 'warn';
}

export interface ShellGroup {
  readonly label: string;
  readonly items: readonly ShellItem[];
}

export interface ConsoleShellProps {
  readonly appName: string;
  /** The one-crumb trail drawn when the screen declares none. */
  readonly fallbackCrumb?: string;
  readonly env: { readonly label: string; readonly live: boolean };
  readonly groups: readonly ShellGroup[];
  readonly collapsed: boolean;
  readonly onCollapsedChange: (next: boolean) => void;
  readonly theme: {
    readonly value: string;
    readonly options: readonly SelectOption[];
    readonly label: string;
  };
  readonly onThemeChange: (value: string) => void;
  readonly locale: string;
  readonly locales: readonly string[];
  readonly onLocaleChange: (value: string) => void;
  readonly me: { readonly name: string; readonly email: string; readonly initials: string };
  readonly onSignOut: () => void;
  readonly labels: {
    readonly collapse: string;
    readonly expand: string;
    readonly signOut: string;
    readonly navigation: string;
    readonly breadcrumbs: string;
    readonly skipToContent: string;
    readonly language: string;
  };
  readonly children: ReactNode;
}

/** The Forecastify mark, the mockup's own asset, served from `public/`. */
const MARK = '/forecastify-mark.png';

const BADGE: Readonly<Record<NonNullable<ShellItem['badgeTone']>, string>> = {
  muted: TONE.muted,
  bad: TONE.bad,
  warn: TONE.warn,
};

/* ⚠️ The dot on the collapsed rail is a solid swatch of the same tone. A muted dot on a muted rail
   would be invisible, so the muted badge's dot is the foreground at reduced strength. */
const DOT: Readonly<Record<NonNullable<ShellItem['badgeTone']>, string>> = {
  muted: 'bg-muted-foreground/60',
  bad: 'bg-destructive',
  warn: 'bg-[var(--status-warn-line)]',
};

export function ConsoleShell(props: ConsoleShellProps): ReactNode {
  const { collapsed, labels } = props;
  const chrome = usePageChromeState();
  const declared = chrome?.crumbs ?? [];
  const crumbs =
    declared.length > 0 || props.fallbackCrumb === undefined
      ? declared
      : [{ label: props.fallbackCrumb }];

  return (
    <div className="flex h-screen w-full overflow-hidden" data-app-root="1">
      <a
        href="#console-content"
        className="bg-primary text-primary-foreground sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:px-3 focus:py-2 focus:text-[12.5px]"
      >
        {labels.skipToContent}
      </a>

      <aside
        className={cn(
          'bg-sidebar text-sidebar-foreground flex shrink-0 flex-col overflow-hidden pb-2 transition-[width] duration-150 ease-[cubic-bezier(.32,.72,0,1)] max-[900px]:hidden',
          collapsed ? 'w-[52px] px-1.5' : 'w-[236px] px-2',
        )}
      >
        {collapsed ? (
          <div className="mb-2 flex h-12 shrink-0 items-center justify-center">
            {/* ⚠️ The mark IS the expand control when collapsed, and it says so on hover: the
                mark slides away and the panel glyph slides in. Verbatim from the mockup. */}
            <Tooltip asChild side="bottom" content={labels.expand}>
              <button
                type="button"
                className="group hover:bg-sidebar-accent relative grid size-8 place-items-center rounded-md transition-colors"
                onClick={() => {
                  props.onCollapsedChange(false);
                }}
                aria-label={labels.expand}
              >
                <img
                  src={MARK}
                  alt=""
                  className="size-5 object-contain transition-[opacity,transform] duration-150 ease-[cubic-bezier(.32,.72,0,1)] group-hover:-translate-x-1 group-hover:opacity-0"
                />
                <span className="absolute inset-0 grid translate-x-1 place-items-center opacity-0 transition-[opacity,transform] duration-150 ease-[cubic-bezier(.32,.72,0,1)] group-hover:translate-x-0 group-hover:opacity-100">
                  <Icon name="panel-left" size={15} />
                </span>
              </button>
            </Tooltip>
          </div>
        ) : (
          <>
            <div className="flex h-12 shrink-0 items-center gap-2 px-1.5">
              <img src={MARK} alt="" className="size-5 shrink-0 object-contain" />
              <span className="min-w-0 flex-1 truncate text-[13px] font-semibold">
                {props.appName}
              </span>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => {
                  props.onCollapsedChange(true);
                }}
                aria-label={labels.collapse}
                title={labels.collapse}
              >
                <Icon name="panel-left" size={15} />
              </Button>
            </div>
            <div className="text-muted-foreground mb-2 flex items-center gap-1.5 px-2 text-[11.5px]">
              <span
                className={cn(
                  'size-1.5 rounded-full',
                  props.env.live ? 'bg-[var(--status-ok-text)]' : 'bg-[var(--status-warn-line)]',
                )}
              />
              <span className="truncate">{props.env.label}</span>
            </div>
          </>
        )}

        <nav
          aria-label={labels.navigation}
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-x-hidden overflow-y-auto"
        >
          {props.groups.map((group) => (
            <div key={group.label} className="flex flex-col gap-0.5">
              {collapsed ? null : (
                <span className="text-muted-foreground px-2 pb-1 text-[11px] font-medium tracking-[.03em] whitespace-nowrap uppercase">
                  {group.label}
                </span>
              )}
              {group.items.map((item) => {
                const tone = item.badgeTone ?? 'muted';
                const hasBadge = item.badge !== undefined && item.badge > 0;
                const state = item.active
                  ? 'bg-sidebar-accent font-medium'
                  : 'hover:bg-sidebar-accent/70';
                return collapsed ? (
                  <Tooltip key={item.href} asChild content={item.label}>
                    <Link
                      to={item.href}
                      aria-current={item.active ? 'page' : undefined}
                      aria-label={item.label}
                      className={cn(
                        'text-sidebar-foreground hover:text-sidebar-foreground relative mx-auto grid size-9 place-items-center rounded-md transition-colors',
                        state,
                      )}
                    >
                      <Icon name={item.icon} size={15} />
                      {hasBadge ? (
                        <span
                          className={cn('absolute top-1 right-1 size-2 rounded-full', DOT[tone])}
                        />
                      ) : null}
                    </Link>
                  </Tooltip>
                ) : (
                  <Link
                    key={item.href}
                    to={item.href}
                    aria-current={item.active ? 'page' : undefined}
                    className={cn(
                      'text-sidebar-foreground hover:text-sidebar-foreground flex h-8 w-full items-center gap-2 rounded-md px-2 text-left text-[13px] transition-colors',
                      state,
                    )}
                  >
                    <Icon name={item.icon} size={15} className="shrink-0" />
                    <span className="min-w-0 flex-1 truncate">{item.label}</span>
                    {hasBadge ? (
                      <span
                        className={cn(
                          'grid h-[18px] min-w-[18px] place-items-center rounded-full px-1 text-[10.5px] font-semibold tabular-nums',
                          BADGE[tone],
                        )}
                      >
                        {item.badge}
                      </span>
                    ) : null}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {collapsed ? (
          <div className="flex flex-col items-center gap-1.5 border-t pt-2">
            <Tooltip asChild content={props.me.name}>
              <span className="bg-primary/15 text-primary grid size-7 place-items-center rounded-full text-[10.5px] font-semibold">
                {props.me.initials}
              </span>
            </Tooltip>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={props.onSignOut}
              aria-label={labels.signOut}
              title={labels.signOut}
            >
              <Icon name="log-out" size={14} />
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-1.5 border-t pt-2">
            <div className="flex items-center gap-1.5">
              <div className="min-w-0 flex-1">
                <Select
                  aria-label={props.theme.label}
                  icon="sun"
                  options={props.theme.options}
                  value={props.theme.value}
                  onChange={(event) => {
                    props.onThemeChange(event.target.value);
                  }}
                  className={cn(SELECT_SKIN, 'h-7 px-2 text-[12.5px]')}
                />
              </div>
              <div
                role="group"
                aria-label={labels.language}
                className="bg-muted flex shrink-0 gap-0.5 rounded-md p-0.5"
              >
                {props.locales.map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={option === props.locale}
                    onClick={() => {
                      props.onLocaleChange(option);
                    }}
                    className={cn(
                      'h-6 rounded px-1.5 text-[11.5px] font-medium',
                      option === props.locale
                        ? 'bg-background text-foreground shadow-xs'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {option.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 px-1.5 py-1">
              <span className="bg-primary/15 text-primary grid size-6 shrink-0 place-items-center rounded-full text-[10.5px] font-semibold">
                {props.me.initials}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[12.5px] font-medium">{props.me.name}</span>
                <span className="text-muted-foreground block truncate text-[11px]">
                  {props.me.email}
                </span>
              </span>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={props.onSignOut}
                aria-label={labels.signOut}
                title={labels.signOut}
              >
                <Icon name="log-out" size={14} />
              </Button>
            </div>
          </div>
        )}
      </aside>

      <main className="flex min-w-0 flex-1 flex-col py-2 pr-2 max-[900px]:p-0">
        <div className="bg-background flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border max-[900px]:rounded-none">
          <header className="flex h-12 shrink-0 items-center gap-2 border-b px-4">
            <nav
              aria-label={labels.breadcrumbs}
              className="flex min-w-0 flex-1 items-center gap-1.5 text-[13px]"
            >
              {crumbs.map((crumb, index) => (
                <span
                  key={`${crumb.label}-${String(index)}`}
                  className="flex min-w-0 items-center gap-1.5"
                >
                  {index > 0 ? (
                    <Icon
                      name="chevron-right"
                      size={13}
                      className="text-muted-foreground shrink-0"
                    />
                  ) : null}
                  {crumb.href === undefined ? (
                    <span
                      aria-current="page"
                      className="min-w-0 truncate rounded px-1 py-0.5 font-medium"
                    >
                      {crumb.label}
                    </span>
                  ) : (
                    <Link
                      to={crumb.href}
                      className="text-muted-foreground hover:text-foreground hover:bg-muted min-w-0 truncate rounded px-1 py-0.5"
                    >
                      {crumb.label}
                    </Link>
                  )}
                </span>
              ))}
            </nav>
            <div ref={chrome?.setActionsSlot} className="flex shrink-0 items-center gap-2" />
          </header>

          <div id="console-content" className="min-h-0 flex-1 overflow-y-auto">
            {props.children}
          </div>
        </div>
      </main>
    </div>
  );
}
